import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/20/solid';
import { EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import { useMemo } from 'react';

import { Button } from '../../ui/catalyst/button';
import {
  Dropdown,
  DropdownButton,
  DropdownDivider,
  DropdownItem,
  DropdownMenu,
} from '../../ui/catalyst/dropdown';
import { ResponsiveBreakpoint, TableAction, TableMenuAction } from './types';

/**
 * Maps breakpoint to Tailwind classes for showing element AT and ABOVE that breakpoint.
 * e.g., 'lg' -> 'hidden lg:flex' (hidden below lg, flex at lg+)
 */
const BREAKPOINT_SHOW_CLASSES: Record<ResponsiveBreakpoint, string> = {
  sm: 'hidden sm:flex',
  md: 'hidden md:flex',
  lg: 'hidden lg:flex',
  xl: 'hidden xl:flex',
  '2xl': 'hidden 2xl:flex',
};

/**
 * Maps breakpoint to Tailwind classes for showing element BELOW that breakpoint.
 * e.g., 'lg' -> 'lg:hidden' (visible below lg, hidden at lg+)
 */
const BREAKPOINT_HIDE_CLASSES: Record<ResponsiveBreakpoint, string> = {
  sm: 'sm:hidden',
  md: 'md:hidden',
  lg: 'lg:hidden',
  xl: 'xl:hidden',
  '2xl': '2xl:hidden',
};

interface DataTableHeaderProps {
  title?: string;
  description?: string;
  searchEnabled?: boolean;
  searchPlaceholder?: string;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  searchResultsText?: string;
  actions?: TableAction[];
  menuActions?: TableMenuAction[];
  totalCount?: number;
  startIndex?: number;
  endIndex?: number;
  selectionEnabled?: boolean;
  selectedCount?: number;
  totalSelectable?: number;
  onClearSelection?: () => void;
  bulkActions?: TableAction[];
  isLoading?: boolean;
  /** Show progress bar on header (can be true independently of isLoading) */
  showProgressBar?: boolean;
}

/**
 * Reusable table header with search, actions, and menu
 */
export const DataTableHeader = ({
  title,
  description,
  searchEnabled = false,
  searchPlaceholder = 'Search...',
  searchValue = '',
  onSearchChange,
  searchResultsText,
  actions = [],
  menuActions = [],
  totalCount,
  startIndex,
  endIndex,
  selectionEnabled = false,
  selectedCount = 0,
  totalSelectable = 0,
  onClearSelection,
  bulkActions = [],
  isLoading = false,
  showProgressBar = false,
}: DataTableHeaderProps) => {
  // Determine if we're in selection mode
  const isSelectionMode = selectionEnabled && selectedCount > 0;

  // Build collapsed actions for overflow menu (actions with collapseAt, excluding customRender)
  const collapsedMenuActions: TableMenuAction[] = useMemo(() => {
    const collapsible = actions.filter(
      (action) => action.collapseAt && !action.customRender && action.onClick,
    );
    if (collapsible.length === 0) return [];

    return collapsible.map((action) => ({
      label: action.label,
      icon: action.icon,
      onClick: action.onClick ?? (() => {}),
      // Add responsive class to only show when collapsed
      className: action.collapseAt
        ? BREAKPOINT_HIDE_CLASSES[action.collapseAt]
        : undefined,
    }));
  }, [actions]);

  // Combine collapsed actions with explicit menu actions
  const allMenuActions: TableMenuAction[] = useMemo(() => {
    if (collapsedMenuActions.length === 0) return menuActions;

    // Add divider between collapsed actions and explicit menu actions
    if (menuActions.length > 0) {
      return [
        ...collapsedMenuActions,
        { label: '', onClick: () => {}, divider: true },
        ...menuActions,
      ];
    }
    return collapsedMenuActions;
  }, [collapsedMenuActions, menuActions]);

  // Check if we need to show the overflow menu
  const hasMenuActions = allMenuActions.length > 0;

  return (
    <div className="px-4 py-2 text-xs border-b border-b-zinc-300 dark:border-b-zinc-700 relative">
      {/* Title row */}
      {(title || description) && (
        <div className="mb-2 flex items-center justify-between">
          <div>
            {title && (
              <h1 className="text-xl font-semibold text-zinc-950 dark:text-white">
                {title}
              </h1>
            )}
            {description && (
              <p className="mt-0.5 text-xs text-zinc-600 dark:text-zinc-400">
                {description}
              </p>
            )}
          </div>
          {totalCount !== undefined &&
            startIndex !== undefined &&
            endIndex !== undefined && (
              <span className="text-xs text-zinc-600 dark:text-zinc-400">
                Showing {startIndex}-{endIndex} of {totalCount} results
              </span>
            )}
        </div>
      )}

      {/* Search and actions row OR Selection mode row */}
      {(searchEnabled ||
        actions.length > 0 ||
        menuActions.length > 0 ||
        isSelectionMode) && (
        <div className="flex items-center gap-4">
          {isSelectionMode ? (
            <>
              {/* Selection mode UI */}
              <button
                type="button"
                onClick={onClearSelection}
                className="flex items-center justify-center h-8 w-8 hover:bg-gray-100 dark:hover:bg-zinc-800 rounded transition-colors"
                aria-label="Clear selection"
              >
                <XMarkIcon className="h-4 w-4 text-gray-700 dark:text-zinc-300" />
              </button>

              <span className="text-xs font-semibold text-black dark:text-white">
                {selectedCount} of {totalSelectable} selected
              </span>

              {/* Bulk action buttons - positioned left for horizontal scaling */}
              <div className="flex items-center gap-3 ml-4">
                {bulkActions.map((action, idx) => {
                  // Custom render takes precedence - used for complex components like date pickers
                  if (action.customRender) {
                    return (
                      <div key={idx}>{action.customRender(selectedCount)}</div>
                    );
                  }

                  if (action.dropdown) {
                    // Generate a key based on dropdown content to force re-render when items change
                    const dropdownKey = `${idx}-${action.dropdown.map((d) => `${d.value}:${d.disabled}`).join(',')}`;
                    return (
                      <Dropdown key={dropdownKey}>
                        <DropdownButton
                          outline
                          className={`whitespace-nowrap !text-xs !font-medium h-8 ${action.className || ''}`}
                        >
                          {action.icon}
                          {action.label}
                        </DropdownButton>
                        <DropdownMenu anchor="bottom start">
                          {action.dropdown.map((item, itemIdx) => (
                            <DropdownItem
                              key={itemIdx}
                              onClick={item.disabled ? undefined : item.onClick}
                              disabled={item.disabled}
                            >
                              {item.label}
                            </DropdownItem>
                          ))}
                        </DropdownMenu>
                      </Dropdown>
                    );
                  }

                  if (action.variant === 'plain') {
                    return (
                      <button
                        key={idx}
                        onClick={action.onClick}
                        type="button"
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-zinc-700 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-md transition-colors whitespace-nowrap ${action.className || ''}`}
                      >
                        {action.icon}
                        {action.label}
                      </button>
                    );
                  }

                  return (
                    <Button
                      key={idx}
                      onClick={action.onClick}
                      outline
                      className={`whitespace-nowrap !text-xs !font-medium h-8 ${action.className || ''}`}
                    >
                      {action.icon}
                      {action.label}
                    </Button>
                  );
                })}
              </div>

              {/* Spacer - pushes everything to the left */}
              <div className="flex-1" />
            </>
          ) : (
            <>
              {/* Normal mode UI */}
              {/* Search - constrained width */}
              {searchEnabled && (
                <div className="w-[350px] relative">
                  <div className="absolute inset-y-0 left-2.5 flex items-center pointer-events-none">
                    <MagnifyingGlassIcon className="h-4 w-4 text-zinc-400 dark:text-zinc-500" />
                  </div>
                  <input
                    type="text"
                    placeholder={searchPlaceholder}
                    value={searchValue}
                    onChange={(e) => onSearchChange?.(e.target.value)}
                    className="h-8 w-full pl-9 pr-8 text-xs font-medium rounded-lg border border-zinc-950/10 bg-white placeholder:text-zinc-500 placeholder:font-medium focus:outline-none focus:border-zinc-950/20 dark:bg-zinc-900 dark:border-white/10 dark:text-white dark:placeholder:text-zinc-400 dark:focus:border-white/20"
                  />
                  {searchValue && (
                    <button
                      type="button"
                      onClick={() => onSearchChange?.('')}
                      className="absolute inset-y-0 right-2 flex items-center text-zinc-400 hover:text-zinc-600 dark:text-zinc-500 dark:hover:text-zinc-300"
                      aria-label="Clear search"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  )}
                </div>
              )}

              {/* Results text */}
              {searchResultsText && (
                <span className="text-xs font-medium text-zinc-600 dark:text-zinc-400">
                  {searchResultsText}
                </span>
              )}

              {/* Spacer */}
              <div className="flex-1" />

              {/* Action buttons */}
              <div className="flex items-center gap-1">
                {actions.map((action, idx) => {
                  // Custom render takes precedence - used for complex components like popovers
                  if (action.customRender) {
                    return <div key={idx}>{action.customRender(0)}</div>;
                  }

                  // Get responsive visibility class if action has collapseAt
                  // When collapseAt is set, use responsive display classes instead of base inline-flex
                  const responsiveClass = action.collapseAt
                    ? BREAKPOINT_SHOW_CLASSES[action.collapseAt]
                    : '';
                  const baseDisplayClass = action.collapseAt
                    ? ''
                    : 'inline-flex';

                  return action.variant === 'plain' ? (
                    <button
                      key={idx}
                      onClick={action.onClick}
                      type="button"
                      className={`${baseDisplayClass} ${responsiveClass} items-center gap-1.5 whitespace-nowrap rounded-md px-2.5 py-1.5 text-xs font-medium text-zinc-600 transition-colors hover:bg-zinc-100 hover:text-zinc-900 dark:text-zinc-400 dark:hover:bg-zinc-800 dark:hover:text-zinc-100 ${action.className || ''}`}
                    >
                      {action.icon}
                      {action.label}
                    </button>
                  ) : (
                    <Button
                      key={idx}
                      onClick={action.onClick}
                      color={action.color || 'blue'}
                      className={`${baseDisplayClass} ${responsiveClass} whitespace-nowrap !text-xs !font-medium h-8 !items-center ${action.className || ''}`}
                    >
                      {action.icon}
                      {action.label}
                    </Button>
                  );
                })}

                {/* Dropdown menu - includes collapsed actions + explicit menu actions */}
                {hasMenuActions && (
                  <Dropdown>
                    <DropdownButton
                      as={Button}
                      plain
                      aria-label="More actions"
                      className="px-2 border border-gray-300 dark:border-zinc-700 h-8 w-8 flex items-center justify-center"
                    >
                      <EllipsisVerticalIcon className="h-4 w-4" />
                    </DropdownButton>
                    <DropdownMenu anchor="bottom end" className="z-50">
                      {allMenuActions.map((menuAction, idx) => {
                        if (menuAction.divider) {
                          // Dividers from collapsed actions need responsive visibility
                          const dividerClass = menuAction.className || '';
                          return (
                            <div key={idx} className={dividerClass}>
                              <DropdownDivider />
                            </div>
                          );
                        }
                        if (menuAction.heading) {
                          return (
                            <div
                              key={idx}
                              className={`px-3.5 pt-2 pb-1 text-[10px] leading-[14px] font-extrabold text-zinc-500 dark:text-zinc-400 uppercase tracking-[1px] ${menuAction.className || ''}`}
                            >
                              {menuAction.label}
                            </div>
                          );
                        }
                        return (
                          <DropdownItem
                            key={idx}
                            onClick={menuAction.onClick}
                            disabled={menuAction.disabled}
                            className={menuAction.className}
                          >
                            <div className="flex w-full flex-row justify-start items-center gap-2">
                              {menuAction.icon}
                              {menuAction.label}
                            </div>
                          </DropdownItem>
                        );
                      })}
                    </DropdownMenu>
                  </Dropdown>
                )}
              </div>
            </>
          )}
        </div>
      )}
      {/* Loading progress bar - thin animated bar on bottom border */}
      {(isLoading || showProgressBar) && (
        <div className="absolute bottom-0 left-0 right-0 h-[2px] overflow-hidden">
          <div className="h-full w-1/3 bg-blue-500 dark:bg-blue-400 rounded-full animate-[datatable-shimmer_1.2s_ease-in-out_infinite]" />
        </div>
      )}
    </div>
  );
};
